import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Idle } from '@ng-idle/core';
import { Store } from '@ngrx/store';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { AuthState, OktaAuth } from '@okta/okta-auth-js';
import { NgxPermissionsService } from 'ngx-permissions';
import {
  BehaviorSubject,
  firstValueFrom,
  Observable,
  of,
  throwError,
} from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { api } from '../../../models/api-url.model';
import {
  GainsightAccount,
  GainsightUser,
} from '../../../models/gainsight.model';
import { User } from '../../../models/user.model';
import { Permission } from '../../../shared/constants/permissions';
import { Logout, State } from '../../../store';
import { GainsightService } from '../gainsight';
import { OrdersParserService } from '../orders-parser';
import type {
  AuthSuccessResponse,
  LoginProviderResponse,
  RegisterRequest,
  RegisterResponse,
  SessionResponse,
} from './auth.types';
import { SESSION_SECONDS, URLS } from './auth.utils';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly oktaAuthStateService = inject(OktaAuthStateService);

  private readonly permissionsService = inject(NgxPermissionsService);

  private readonly ordersParseService = inject(OrdersParserService);

  private readonly gainsightService = inject(GainsightService);

  private readonly oktaAuth = inject<OktaAuth>(OKTA_AUTH);

  private readonly store = inject<Store<State>>(Store);

  private readonly httpClient = inject(HttpClient);

  private readonly matDialog = inject(MatDialog);

  private readonly router = inject(Router);

  private readonly idle = inject(Idle);

  private loggedInUser: User | null = null;

  private hasCompletedOktaLogin = false;

  resetTokenSubject = new BehaviorSubject<string>(null);

  redirectUrl: string | null = null;

  isImpersonated: boolean = false;

  get currentResetToken(): string {
    return this.resetTokenSubject.getValue();
  }

  get currentUser(): User | null {
    return this.loggedInUser;
  }

  private getPermissionsFromUser(user: User): string[] {
    return (user?.Permissions as string[]) ?? [];
  }

  private isOktaUser(user: User): boolean {
    return (user.OktaUsername__c?.length ?? 0) > 0;
  }

  private applySessionToState(
    data: SessionResponse,
    options: {
      isImpersonation?: boolean;
      rememberMe?: boolean;
      redirectAfterLogin?: boolean;
    } = {},
  ): void {
    const user = data?.user;
    if (!user?.Id) return;

    this.loggedInUser = user;
    if (options.isImpersonation) {
      this.isImpersonated = true;
    }
    this.permissionsService.loadPermissions(this.getPermissionsFromUser(user));
    this.trackUser(user, options.isImpersonation ?? false);

    if (options.rememberMe !== undefined) {
      const sessionSeconds = options.rememberMe
        ? SESSION_SECONDS.REMEMBER_ME
        : SESSION_SECONDS.DEFAULT;
      this.idle.setIdle(sessionSeconds);
    }
    this.idle.watch();

    if (options.redirectAfterLogin) {
      if (this.redirectUrl) {
        this.router.navigateByUrl(this.redirectUrl);
      } else {
        this.router.navigate(['/']);
      }
    }
  }

  private buildImpersonateHeaders(token: string): HttpHeaders {
    return new HttpHeaders({
      'Salesforce-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Cache-control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
    });
  }

  async isLoggedIn(): Promise<boolean> {
    if (this.loggedInUser?.Id) {
      return true;
    }

    try {
      const session = await firstValueFrom(
        this.httpClient.get<SessionResponse>(
          `${environment.apiUrl}${api.sessionURL}`,
          {
            withCredentials: true,
          },
        ),
      );

      if (session?.user?.Id) {
        this.loggedInUser = session.user;
        const permissions = (session.user.Permissions as string[]) ?? [];
        this.permissionsService.loadPermissions(permissions);
        return true;
      }
    } catch {
      // ignore and fall through
    }

    const hasSfSession = !!this.loggedInUser?.Id;
    const hasOktaSession = await this.oktaAuth.isAuthenticated();

    return hasSfSession || (hasOktaSession && hasSfSession);
  }

  impersonate(impersonationToken: string): Observable<SessionResponse> {
    const options = {
      headers: this.buildImpersonateHeaders(impersonationToken),
      withCredentials: true,
    };

    return this.httpClient
      .post<AuthSuccessResponse>(URLS.IMPERSONATION, {}, options)
      .pipe(
        switchMap(() => this.getSession()),
        map((data) => {
          const userImpersonated = data?.user;
          if (!userImpersonated?.Id) return null;
          if (this.isOktaUser(userImpersonated)) return null;

          this.applySessionToState(data, { isImpersonation: true });
          return data;
        }),
        catchError((error: HttpErrorResponse) => throwError(() => error)),
      );
  }

  login(
    uid: string,
    password: string,
    rememberMe: boolean,
  ): Observable<SessionResponse> {
    return this.httpClient
      .post<AuthSuccessResponse>(
        URLS.LOGIN,
        { uid, password, rememberMe },
        { withCredentials: true },
      )
      .pipe(
        switchMap(() => this.getSession()),
        map((data) => {
          this.applySessionToState(data, {
            rememberMe,
            redirectAfterLogin: true,
          });
          return data;
        }),
        catchError((error: HttpErrorResponse) => throwError(() => error)),
      );
  }

  getSession(): Observable<SessionResponse> {
    return this.httpClient.get<SessionResponse>(
      `${environment.apiUrl}${api.sessionURL}`,
      {
        withCredentials: true,
      },
    );
  }

  switchAccount(accountId: string): Observable<void> {
    return this.httpClient.post<void>(
      `${environment.apiUrl}${api.switchAccountURL}`,
      { accountId },
      { withCredentials: true },
    );
  }

  trackUser(user: User, isImpersonation: boolean = false): void {
    if (!user?.Id) {
      this.gainsightService.initGainsight({
        user: {
          isLoggedIn: false,
        } as unknown as GainsightUser,
        account: {} as unknown as GainsightAccount,
      });
      return;
    }
    this.gainsightService.initGainsight({
      user: {
        id: user?.Id,
        firstName: user?.FirstName,
        lastName: user?.LastName,
        email: user?.Email,
        role: user.Portal_Role__r?.Name,
        isImpersonation,
        isOktaUser:
          this.isOktaUser(user) || !!user?.Account?.Okta_Login_Enabled__c,
        isIQUser:
          user?.Permissions?.includes(Permission.NITEL_IQ_PARTICIPANT) ?? false,
        isLoggedIn: true,
      },
      account: { id: user?.AccountId, name: user?.Account?.Name },
    });
  }

  signIn(): Observable<SessionResponse> {
    return this.httpClient
      .get<AuthSuccessResponse>(URLS.LOGIN_OKTA_USER, {
        withCredentials: true,
      })
      .pipe(
        switchMap(() => this.getSession()),
        map((data) => {
          this.applySessionToState(data);
          return data;
        }),
        catchError((error: HttpErrorResponse) => throwError(() => error)),
      );
  }

  async signOut(reason: string = null): Promise<void> {
    this.clearCache();
    const postLogoutRedirectUri = reason
      ? `${window.location.origin}/login?logout=${encodeURIComponent(reason)}`
      : `${window.location.origin}/login`;

    await this.oktaAuth.signOut({
      revokeAccessToken: true,
      revokeRefreshToken: true,
      postLogoutRedirectUri,
    });
  }

  async loginWithOkta(username: string = ''): Promise<void> {
    await this.oktaAuth.signInWithRedirect({
      loginHint: username,
      originalUri: '/login/callback',
    });
  }

  clearCache(): void {
    this.httpClient
      .post(`${environment.apiUrl}/auth/logout`, {}, { withCredentials: true })
      .pipe(
        take(1),
        catchError(() => of(null)),
      )
      .subscribe();

    this.permissionsService.flushPermissions();
    this.store.dispatch(new Logout());
    this.matDialog.closeAll();
    this.ordersParseService.clear();
    this.loggedInUser = null;

    localStorage.clear();
    this.oktaAuth.tokenManager.clear();

    // stop watching idle session
    this.idle.stop();
  }

  forgotPassword(uid: string): Observable<void> {
    this.idle.stop();
    return this.httpClient.post<void>(URLS.FORGOT_PASSWORD, { uid });
  }

  register(request: RegisterRequest): Observable<RegisterResponse> {
    return this.httpClient
      .post<RegisterResponse>(URLS.REGISTER, request)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  resetPassword(
    token: string,
    password: string,
    passwordConfirmation: string,
  ): Observable<boolean> {
    this.idle.stop();
    this.resetTokenSubject.next(token);
    return this.httpClient
      .post<boolean>(URLS.RESET_PASSWORD, {
        password,
        passwordConfirmation,
      })
      .pipe(
        map(() => true),
        catchError(() => of(false)),
      );
  }

  validateResetToken(token: string): Observable<boolean> {
    const headers = new HttpHeaders({
      'Salesforce-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '-1',
    });

    return this.httpClient.get<void>(URLS.VALIDATE_PASSWORD, { headers }).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  checkLoginProvider(
    username: string,
  ): Observable<boolean | LoginProviderResponse> {
    return this.httpClient
      .post<boolean>(`${environment.apiUrl}${api.checkAuthProviderURL}`, {
        username,
      })
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  completeOktaLogin(): void {
    if (this.hasCompletedOktaLogin) return;
    this.hasCompletedOktaLogin = true;

    this.oktaAuthStateService.authState$
      .pipe(
        filter((authState: AuthState) => authState?.isAuthenticated === true),
        take(1),
        switchMap(() => this.signIn().pipe(take(1))),
      )
      .subscribe({
        next: () => {
          const redirectUrl = localStorage.getItem('redirectUrl');
          localStorage.removeItem('redirectUrl');

          if (redirectUrl) {
            this.router.navigateByUrl(redirectUrl);
          } else {
            this.router.navigate(['/']);
          }
        },
        error: async () => {
          await this.signOut();
        },
      });
  }
}
